import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { TenantService } from '@core/tenant/tenant.service';
import { TranslocoService } from '@ngneat/transloco';
import { ApiService } from '@targx/services/api/api.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'environments/environment';

const SCROLL_THRESHOLD = 20;

@Component({
  selector: 'ade-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class AdeHeaderComponent implements OnInit, OnDestroy {
  navigation: { site: any[] } | null = null;
  activeLang: string;
  mobileMenuOpen = false;
  searchOpen = false;
  searchQuery = '';
  scrolled = false;

  private _unsub = new Subject<void>();

  constructor(
    private _tenantService: TenantService,
    private _translocoService: TranslocoService,
    private _api: ApiService,
    private _router: Router,
    @Inject(PLATFORM_ID) private _platformId: object,
  ) {}

  get logoUrl(): string {
    const url = this._tenantService.profile?.dealerSite?.branding?.logo?.url;
    if (!url) return 'assets/images/logo/logo-1.png';
    return url.startsWith('http') ? url : `${environment.assets}${url}`;
  }

  ngOnInit(): void {
    this.activeLang = this._translocoService.getActiveLang();
    this._loadMenu();

    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._unsub),
      )
      .subscribe(() => {
        this.mobileMenuOpen = false;
        this.searchOpen = false;
      });
  }

  ngOnDestroy(): void {
    this._unsub.next();
    this._unsub.complete();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    this.scrolled = window.scrollY > SCROLL_THRESHOLD;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }
  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  toggleSearch(): void {
    if (this.searchOpen && this.searchQuery.trim()) {
      this.submitSearch();
      return;
    }
    this.searchOpen = !this.searchOpen;
    if (!this.searchOpen) this.searchQuery = '';
  }

  submitSearch(): void {
    const query = this.searchQuery.trim();
    if (!query) return;
    this._router.navigate(['/', this.activeLang, 'pesquisa'], {
      queryParams: { s: query, page: 1 },
    });
  }

  private _loadMenu(): void {
    const lang = this._translocoService.getActiveLang()?.split('-')[0] || 'pt';
    const menuKey = this._tenantService.profile?.dealerSite?.menuKey;

    // 1 — menu inline no profile
    const inlineItems =
      this._tenantService.profile?.dealerSite?.menu?.items?.data;
    if (inlineItems?.length) {
      this.navigation = {
        site: inlineItems.map((i) => this._toNavItem(i, lang)),
      };
      return;
    }

    // 2 — fetch por menuKey
    if (!menuKey) return;
    const slug = encodeURIComponent(`${menuKey}-${lang}`);
    this._api
      .get(`/menus/?nested&populate=*&filters%5Bslug%5D=${slug}`)
      .pipe(takeUntil(this._unsub))
      .subscribe((res: any) => {
        const items = res?.data?.[0]?.attributes?.items?.data ?? [];
        this.navigation = {
          site: items.map((i: any) => this._toNavItem(i, lang)),
        };
      });
  }

  private _toNavItem(item: any, lang: string): any {
    const url = item.attributes?.url || '';
    return {
      id: item.id,
      title: item.attributes?.title,
      type: item.attributes?.children?.data?.length ? 'group' : 'basic',
      link: `/${lang}${url}`,
      target: item.attributes?.target,
      children: (item.attributes?.children?.data || []).map((c: any) =>
        this._toNavItem(c, lang),
      ),
    };
  }
}
